import {
  Button,
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  Text,
  LoadingOrbit,
} from '@chatsdet/ui';
import { usePageSkillVersions } from '../usePageSkills';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  propertyId: string;
  skillId: string | null;
  pathPattern: string;
};

/**
 * Every version of a skill, newest first — agent writes and groomer compactions
 * alike. This is what makes editing safe to offer: a bad human edit (or a bad
 * groom) is always readable here, so nothing an agent learned is ever lost.
 */
export function PageSkillVersionsDialog({
  isOpen,
  onClose,
  propertyId,
  skillId,
  pathPattern,
}: Props) {
  const { data: versions, isLoading } = usePageSkillVersions(propertyId, isOpen ? skillId : null);

  return (
    <DialogTrigger
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Button className="hidden">Trigger</Button>
      <Dialog title="Version history" description={pathPattern}>
        <DialogContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <LoadingOrbit />
            </div>
          ) : !versions?.length ? (
            <Text className="text-muted-foreground">No versions recorded.</Text>
          ) : (
            <div className="divide-y divide-border">
              {versions.map((v) => (
                <div key={v.id} className="py-3 space-y-2 first:pt-0 last:pb-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <Text size="sm" className="font-medium">
                      v{v.version} — {v.title}
                    </Text>
                    <Text size="xs" className="text-muted-foreground shrink-0">
                      {new Date(v.createdAt).toLocaleString()} · {v.body.length}B
                    </Text>
                  </div>
                  <Text size="xs" className="text-muted-foreground">
                    {v.summary}
                  </Text>
                  <pre className="text-xs whitespace-pre-wrap break-words max-h-48 overflow-y-auto rounded bg-muted/40 p-2">
                    {v.body}
                  </pre>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onPress={onClose}>
            Close
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
